import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type JSX,
  type ReactNode,
} from 'react';

/**
 * A lightweight disclosure — a titled row that expands to reveal its children.
 * Collapsed by default (so heavy content like the login audit or the passkey
 * list doesn't dominate the drawer). Accessible: the trigger is a real button
 * with aria-expanded + aria-controls. Self-styled with tokens + fallbacks.
 *
 * PAGE-DISCLOSURE OPTIONS (cca-g55xr, first used by the public complaints guide):
 * `headingLevel`, `keepMounted`, `open`/`onOpenChange`, `description`, `id` and
 * `className`. Every one is optional, and with none of them set the component
 * renders exactly as it always has — the drawer row, its panel unmounted while
 * closed — so the drawer callers are untouched. They exist so a page can use
 * this one disclosure (UX-1, ARCH-11) instead of forking it: a step in a
 * numbered list needs a real heading, a panel that find-in-page can still reach
 * while closed, a summary the trigger names, and a look of its own.
 */
export interface CollapsibleProps {
  title: ReactNode;
  children: ReactNode;
  /** Start open (default false — collapsed). Ignored when `open` is given. */
  defaultOpen?: boolean;
  /** Optional right-aligned hint (e.g. a count) shown on the trigger row. */
  hint?: ReactNode;
  /** Put the trigger inside an `<h2>`–`<h6>`, so the disclosure sits in the page's
   *  heading outline (a button inside a heading). Omitted: no heading, as in the drawer. */
  headingLevel?: 2 | 3 | 4 | 5 | 6;
  /** Keep the panel in the DOM while closed, carrying `hidden="until-found"`: the
   *  browser's find-in-page still reaches its text, and the `beforematch` it fires
   *  opens the disclosure. Omitted: the panel unmounts while closed (the drawer's
   *  lazy body — nothing inside it mounts until asked for). */
  keepMounted?: boolean;
  /** Controlled open state. With it the trigger only ASKS, through `onOpenChange`. */
  open?: boolean;
  /** Called with the state the trigger (or a find-in-page match) asks for. */
  onOpenChange?: (open: boolean) => void;
  /** A short summary shown under the trigger, open or closed: outside the heading,
   *  and named by the trigger's `aria-describedby`. */
  description?: ReactNode;
  /** A stable id stem: the panel is `id`, the trigger `${id}-button` and the
   *  description `${id}-summary`. Omitted: generated, and the trigger has no id. */
  id?: string;
  /** Hand the look to the caller's stylesheet. Set, the component draws nothing
   *  itself — no inline style anywhere and an empty chevron for the caller to draw —
   *  and marks its parts with `data-part` (head, heading, trigger, chevron, title,
   *  hint, description, panel) and its root with this class and
   *  `data-state="open|closed"`. Omitted: the drawer look, unchanged. */
  className?: string;
}

let uid = 0;

export function Collapsible({
  title,
  children,
  defaultOpen = false,
  hint,
  headingLevel,
  keepMounted = false,
  open: openProp,
  onOpenChange,
  description,
  id: idStem,
  className,
}: CollapsibleProps): JSX.Element {
  const [ownOpen, setOwnOpen] = useState(defaultOpen);
  const [generatedId] = useState(() => `cca-collapsible-${(uid += 1)}`);
  const controlled = openProp !== undefined;
  const open = openProp ?? ownOpen;
  const id = idStem ?? generatedId;
  const styled = className === undefined;
  const panelRef = useRef<HTMLDivElement>(null);

  /** The caller's look marks each part; the drawer look marks none (its DOM is unchanged). */
  const part = (name: string): string | undefined => (styled ? undefined : name);

  const request = (next: boolean): void => {
    if (!controlled) setOwnOpen(next);
    onOpenChange?.(next);
  };

  // React writes `hidden` as a boolean attribute (`hidden=""`), so the
  // until-found value is set here, after React's own write, on every close.
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (keepMounted && !open && panel) panel.setAttribute('hidden', 'until-found');
  }, [keepMounted, open]);

  // A find-in-page match inside a closed panel: the browser fires `beforematch`
  // and removes `hidden` itself; the open state follows it. Re-subscribed on
  // every render so the handler always sees the current props.
  useEffect(() => {
    const panel = panelRef.current;
    if (!keepMounted || open || !panel) return;
    const reveal = (): void => request(true);
    panel.addEventListener('beforematch', reveal);
    return () => panel.removeEventListener('beforematch', reveal);
  });

  const trigger: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    background: 'transparent',
    border: 0,
    padding: '8px 0',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--color-ink, #111827)',
    textAlign: 'left',
  };
  const chevron: CSSProperties = {
    display: 'inline-block',
    transition: 'transform var(--duration-fast, 120ms) var(--ease-standard, ease)',
    transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
    fontSize: 10,
    color: 'var(--color-muted, #6b7280)',
    width: 12,
    flexShrink: 0,
  };
  const muted: CSSProperties = {
    fontSize: 12,
    fontWeight: 400,
    color: 'var(--color-muted, #6b7280)',
  };
  const panelPadding: CSSProperties = { padding: '4px 0 8px' };
  const descId = description != null ? `${id}-summary` : undefined;

  const button = (
    <button
      type="button"
      id={idStem === undefined ? undefined : `${idStem}-button`}
      style={styled ? trigger : undefined}
      data-part={part('trigger')}
      aria-expanded={open}
      aria-controls={id}
      aria-describedby={descId}
      onClick={() => request(!open)}
    >
      <span aria-hidden="true" style={styled ? chevron : undefined} data-part={part('chevron')}>
        {styled ? '▶' : null}
      </span>
      <span style={styled ? { flex: 1 } : undefined} data-part={part('title')}>
        {title}
      </span>
      {hint != null && (
        <span style={styled ? muted : undefined} data-part={part('hint')}>
          {hint}
        </span>
      )}
    </button>
  );

  const Heading = headingLevel === undefined ? null : (`h${headingLevel}` as const);
  const heading = Heading ? (
    <Heading style={styled ? { margin: 0 } : undefined} data-part={part('heading')}>
      {button}
    </Heading>
  ) : (
    button
  );

  const summary =
    description != null ? (
      <p
        id={descId}
        style={styled ? { ...muted, margin: '0 0 4px' } : undefined}
        data-part={part('description')}
      >
        {description}
      </p>
    ) : null;

  // The head wraps the heading and its summary, so a caller can stretch the
  // trigger's hit area over both. The drawer row without a summary has no head.
  const head =
    summary !== null || !styled ? (
      <div data-part={part('head')}>
        {heading}
        {summary}
      </div>
    ) : (
      heading
    );

  let panel: ReactNode = null;
  if (keepMounted) {
    // The drawer's padding goes on an inner box: Chrome lays a closed until-found
    // panel out with `content-visibility: hidden`, which keeps the panel's own box,
    // so padding on the panel itself would show as a gap while it is closed.
    panel = (
      <div id={id} ref={panelRef} hidden={!open} data-part={part('panel')}>
        {styled ? <div style={panelPadding}>{children}</div> : children}
      </div>
    );
  } else if (open) {
    panel = (
      <div id={id} style={styled ? panelPadding : undefined} data-part={part('panel')}>
        {children}
      </div>
    );
  }

  return (
    <div className={className} data-state={styled ? undefined : open ? 'open' : 'closed'}>
      {head}
      {panel}
    </div>
  );
}
